// AppShellHost — the mount of the `journal` module (the Journal tab). The
// shell edge for the module: it builds a runtime once per Journal mount,
// mounts the module with the real JournalPort and this device's draft stash,
// projects its Model into React, and tells it the Journal came into view.

import { useEffect, useState, useSyncExternalStore } from "react";
import {
    type DraftStash,
    type JournalDeps,
    type JournalModel,
    type JournalMsg,
    mkJournalModule,
} from "../../modules/journal.js";
import {
    readDeviceKey,
    removeDeviceKey,
    writeDeviceKey,
} from "../deviceStore.js";
import { mkRuntime } from "../runtime.js";

export type Journal = {
    readonly model: JournalModel;
    readonly send: (msg: JournalMsg) => void;
};

// An unsent "Du" draft waits on this device until the server has it.
const draftKey = (periodId: string): string => `bilda.journalDraft.${periodId}`;
const deviceStash: DraftStash = {
    read: (id) => readDeviceKey(draftKey(id)),
    write: (id, text) => writeDeviceKey(draftKey(id), text),
    remove: (id) => removeDeviceKey(draftKey(id)),
};

// After the frame that renders it (a rollup's child opened at its own scale).
const revealInDocument = (anchor: string): void => {
    requestAnimationFrame(() =>
        document.getElementById(anchor)?.scrollIntoView({ block: "start" }),
    );
};

export const useJournal = (ports: JournalDeps["ports"]): Journal => {
    const [mount] = useState(() => {
        const runtime = mkRuntime();
        return {
            runtime,
            journal: runtime.mount(
                mkJournalModule({
                    ports,
                    stash: deviceStash,
                    reveal: revealInDocument,
                }),
            ),
        };
    });
    const { runtime, journal } = mount;
    const model = useSyncExternalStore(runtime.subscribe, journal.getModel);
    const send = (msg: JournalMsg): void => {
        const r = journal.dispatch(msg);
        if (r._tag === "Result.Err" && import.meta.env.DEV)
            console.debug("[journal] refused", r.error.type, msg.type);
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: once per mount.
    useEffect(() => send({ type: "journal_opened" }), []);
    return { model, send };
};
